import React, { useEffect, useState } from 'react';
import { withSession } from '../../session';
import ProductionProjects from './productionProjects';
import axios from 'axios';
import { get, uniqueId, uniqBy } from "lodash";
import {
	SdfButton, SdfPageHeaderLayout, SdfFocusPane, SdfBusyIndicator
} from '@synerg/react-components';
import { DevelopmentProjects } from './developmentProjects';
import { ContactsProject } from './contactsProject';
import ProjectIllustration from './projectIllustration';
import { ProjectSteps } from './projectsSteps';
import WhatToExpect from './whatToExpect';
import CertificateAlert from '../../certificate/certificateAlert';
import Toast from '../../common/toast';
import ProjectStepsDC from './projectStepsDC';
import ProjectStepsB2C from './projectStepsB2C';

function Projects({ session = {} }) {
	const [fullName, setFullName] = useState('');
	const [partnerName, setPartnerName] = useState('');

	const [projectList, setProjectList] = useState([]);
	const [productList, setProductList] = useState([]);
	const [prodList, setProdList] = useState([]);
	const [devList, setDevList] = useState([]);
	const [contacts, setContacts] = useState([]);

	const [jumpScrollIdProd, setJumpScrollIdProd] = useState(null);
	const [jumpScrollIdDev, setJumpScrollIdDev] = useState(null);

	const [showCreateProjectModal, setCreateProjectModal] = useState();
	const [loading, setLoading] = useState(false);
	const [createProjectDetails, setCreateProjectDetails] = useState({});
	const [toast, setToast] = useState(false);
	const [isB2CPartner, setIsB2CPartner] = useState(false);

	const isDataCloudUser = session.roles.includes('selfservicedataclouduser');

	const handleError = (message) => {
		setToast({
			key: uniqueId(),
			status: "error",
			message: message,
			showToast: true
		});
	};

	useEffect(() => {
		if (session) {
			const firstInitial = session.given_name.charAt(0).toUpperCase();
			const firstName = firstInitial + session.given_name.slice(1);
			const lastInitial = session.family_name.charAt(0).toUpperCase();
			const lastName = lastInitial + session.family_name.slice(1);
			setPartnerName(session.orgName || '');
			setFullName(firstName + ' ' + lastName);
		}
	}, [session])

	useEffect(() => {
		if (!showCreateProjectModal) {
			setProductList([]);
			setProjectList([]);
			getProducts(session);
			fetchContacts();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showCreateProjectModal]);

	useEffect(() => {
		// Fetch partner data to check if B2C
		async function fetchPartnerData() {
			if (!session?.ooid) return;
			
			try {
				const { data: partner } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/partner/${session.ooid}`);
				setIsB2CPartner(partner.fdxSupport === true);
			} catch (e) {
				console.error('Error fetching partner data:', e);
				// Don't block the UI if this fails
			}
		}
		
		fetchPartnerData();
	}, [session.ooid]);

	const fetchContacts = async () => {
		try {
			// was /api/my-contacts
			const result = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/contacts`);
			setContacts(result.data);
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get contacts. Please try again.');
			alert(`${errorMessage}`);
		}
	};

	const getProjects = async (products) => {
		try {
			const response = await axios.get(`api/projects`);
			const data = get(response, 'data', {});
			if (data) {
				data.forEach((project) => {
					if (project.fdxSupport) {
						project.product = 'ADP B2C Integration';
					} else {
						const product = products.find(item => item.value === project.size);
						project.product = (product ? product.label || product.name : 'Other');
					}
				});
				setProdList(data.filter(project => project.type === "PROD"));
				setDevList(data.filter(project => project.type === "DEV"));
				setProjectList(data); // all projects
			}
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching project details. Please try again.');
			alert(`${errorMessage}`);
		}
		setLoading(false);
	};

	const getProducts = async (session) => {
		setLoading(true);
		try {
			const isDataCloudUser = session.roles.includes('selfservicedataclouduser');
			const response = await axios.get(`api/products`, { params: { includeDataCloud: isDataCloudUser } })
			const data = get(response, 'data', {});

			if (data) {
				const allProducts = formattedProducts(data);
				setProductList(uniqBy([...allProducts, {label: 'Other', value: 'other'}], 'value'));
				getProjects(allProducts);
			}
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching project details. Please try again.');
			alert(`${errorMessage}`);
		}
	};

	const formattedProducts = (adpProducts) => {
		return adpProducts ? adpProducts.map((product, index) => ({
			key: index,
			type: 'sor',
			value: product.sizeName,
			label: product.name,
			numberOfProjectsFound: 0,
			showLabel: ''
		})) : [];
	};

	return (
    <div>
      {toast && (
        <Toast
          key={toast.key}
          status={toast.status}
          message={toast.message}
          showToast={toast.showToast}
        />
      )}
      {/* Info section */}
      <div style={{ margin: "-0.6rem", overflow: 'auto', maxHeight: '12rem' }}>
        <CertificateAlert
          isProjectsPage={true}
          isDisplayed={false}
          handleError={handleError}
        />
      </div>

      {!loading && projectList && projectList.length > 0 && (
        <div>
          {/* Development projects content section */}
          <div className="flex-col mx-24 mb-10 mt-4 items-center">
            <div className="grid grid-flow-row-dense grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
              <div className="col-span-1">
                <SdfPageHeaderLayout
                  page-title={"Projects (" + projectList.length + ")"}
                ></SdfPageHeaderLayout>
              </div>
              <div className="grid col-span-1 justify-end">
                <SdfButton
                  action="standard"
                  emphasis="primary"
                  size="sm"
                  variant="contained"
                  onClick={() => setCreateProjectModal(true)}
                >
                  Create new project
                </SdfButton>
              </div>
            </div>

            <DevelopmentProjects
              devProjects={devList}
              products={productList}
              jumpScrollIdDev={jumpScrollIdDev}
              setJumpScrollIdProd={setJumpScrollIdProd}
              setJumpScrollIdDev={setJumpScrollIdDev}
              isDataCloudUser={isDataCloudUser}
            />
          </div>

          {/* Production Projects */}
          <div className={"flex-col mx-24 my-10 items-center"}>
            {prodList && prodList.length > 0 && (
              <ProductionProjects
                projects={prodList}
                products={productList}
                fullName={fullName}
                partnerName={partnerName}
                jumpScrollIdProd={jumpScrollIdProd}
                setJumpScrollIdDev={setJumpScrollIdDev}
                setJumpScrollIdProd={setJumpScrollIdProd}
                isDataCloudUser={isDataCloudUser}
              />
            )}
          </div>
        </div>
      )}

		<div>
			{loading &&
				<SdfBusyIndicator
					style={{ color: "#1a8099" }}
					size="xl"
					fullScreen={true}
				></SdfBusyIndicator>}
		</div>
      {/*Create new projects landing page if no projects listed*/}
      {!loading && projectList && projectList.length === 0 && (
        <ProjectIllustration fullName={fullName} partnerName={partnerName} setCreateProjectModal={setCreateProjectModal} />
      )}

      {/* Contacts Project */}
      <div className={"flex-col mx-24 my-10 items-center"}>
        <ContactsProject contacts={contacts} />
      </div>

      {!isDataCloudUser && <WhatToExpect />}

      {showCreateProjectModal && (
        <div>
          <SdfFocusPane
            dismissLabel="Cancel"
            acceptLabel="Next"
            spacing="xl"
            visible={true}
            size="xl"
            hideFooter={true}
          >
            <div className="p-2 pt-4">
              <span slot="title" className="text-heading-06 font-black">
                Create new project
              </span>
              {/* Regular marketplace projects (not DataCloud, not B2C partner) */}
              {!isDataCloudUser && !isB2CPartner && (
                <ProjectSteps
                  hideModal={setCreateProjectModal}
                  createProjectDetails={createProjectDetails}
                  setCreateProjectDetails={setCreateProjectDetails}
                  isDataCloudProject={isDataCloudUser}
                  isB2CProject={isB2CPartner}
                />
              )}
              
              {/* B2C Partner projects - use the new B2C wizard */}
              {isB2CPartner && (
                <ProjectStepsB2C
                  hideModal={setCreateProjectModal}
                  createProjectDetails={createProjectDetails}
                  setCreateProjectDetails={setCreateProjectDetails}
                  isB2CProject={true}
                />
              )}
              
              {/* DataCloud projects - use the DC wizard */}
              {isDataCloudUser && !isB2CPartner && (
                <ProjectStepsDC
                  hideModal={setCreateProjectModal}
                  createProjectDetails={createProjectDetails}
                  setCreateProjectDetails={setCreateProjectDetails}
                  isDataCloudProject={isDataCloudUser}
                />
              )}
            </div>
          </SdfFocusPane>
        </div>
      )}
    </div>
  );
}

export default withSession(Projects);
